<template>
  <article class="listing-map-card__item" @click="onClick">
    <div class="listing-map-card__media">
      <!-- lazy, so the hidden media on narrow maps does not fetch the image -->
      <img v-if="image" class="listing-map-card__image" :src="image" alt="" loading="lazy" />
      <div v-else class="listing-map-card__image listing-map-card__image--empty">
        <SvgIcon name="home" :size="28" />
      </div>
    </div>

    <div class="listing-map-card__body">
      <!-- The chip from ProjectCard, the same reuse the project detail panel
           makes: the project status vocabulary is its own and the dot colour
           carries it. It lives in the body, not the media, because the compact
           layout on narrow maps shows it in flow; the wide layout floats it
           over the media with CSS. -->
      <span
        v-if="project?.status"
        class="project-card__status listing-map-card__status"
        :data-status="project.status"
      >
        <span class="project-card__status-dot" />
        {{ project.status }}
      </span>
      <template v-if="project">
        <h3 class="listing-map-card__title">
          <a :href="project.url" target="_blank" rel="noopener" class="listing-map-card__link">
            {{ project.project }}
          </a><span v-if="project.subProject" class="listing-map-card__phase"> – {{ project.subProject }}</span>
        </h3>
        <p v-if="locationLabel" class="listing-map-card__location">
          <SvgIcon name="pin" :size="14" />
          <span>{{ locationLabel }}</span>
        </p>
        <ul v-if="specs.length" class="listing-map-card__specs">
          <li v-for="spec in specs" :key="spec.icon" class="listing-map-card__spec" :aria-label="spec.aria">
            <SvgIcon :name="spec.icon" :size="14" />
            <span aria-hidden="true">{{ spec.value }}</span>
          </li>
        </ul>
      </template>
      <p v-else-if="error" class="listing-map-card__state">{{ t('project-detail.error') }}</p>
      <div v-else class="listing-map-card__skeleton" aria-hidden="true">
        <span class="listing-card-skeleton__line listing-card-skeleton__line--title" />
        <span class="listing-card-skeleton__line listing-card-skeleton__line--location" />
        <span class="listing-card-skeleton__line" />
      </div>
    </div>
  </article>
</template>

<script setup>
import { computed } from 'vue';
import SvgIcon from './SvgIcon.vue';
import { projectSpecs } from '../lib/projectSpecs';
import { t } from '../lib/i18n';
import { firstImageUrl } from '../lib/mapCardImage';

// One project (phase) in the projects map card. The detail payload arrives
// after the card opened, so the media and body render a skeleton until then.
const props = defineProps({
  project: { type: Object, default: null },
  error: { type: Boolean, default: false }
});

const emit = defineEmits(['select']);

// The whole item opens the detail panel, the same interaction ProjectCard
// has. The title keeps its href so cmd/ctrl-click and middle-click still reach
// the project page; only a plain click is intercepted.
const onClick = (event) => {
  if (!props.project) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  emit('select', props.project);
};

const image = computed(() => firstImageUrl(props.project?.images));

const locationLabel = computed(() =>
  [props.project?.city, props.project?.region].filter(Boolean).join(', ')
);

const specs = computed(() => (props.project ? projectSpecs(props.project) : []));
</script>
